import "mapbox-gl-compare";
import "mapbox-gl-compare/dist/mapbox-gl-compare.css";
import { Box } from "@mui/material";
import { useEffect, useState } from "react";
import { useMapState } from "contexts/MapStateContext";
import {
  addRiskMapSource,
  addRiskMapLayers,
  updateLayerVisibility,
  updateScenarioFilters,
  removeRiskMapLayers,
} from "utils/riskMap";
import RiskComparison from "./RiskComparison";
import MapControlsAccordion from "./MapControlsAccordion";
import { TILE_VERSIONS } from "constants/tileVersions";

const RiskMap = () => {
  const { map, mapboxGeocoderControl } = useMapState();
  const [isMapReady, setIsMapReady] = useState(false);
  const [tileVersion, setTileVersion] = useState(TILE_VERSIONS[0].value);
  const [tilesScenario, setTilesScenario] = useState("combined");
  const [tilesFilterLayers, setTilesFilterLayers] = useState([
    "risk-rating",
    "fire-pathways",
    "conflagration-blocks",
  ]);
  const [leftVersion, setLeftVersion] = useState(TILE_VERSIONS[1].value);
  const [compareMode, setCompareMode] = useState(false);
  const [hoveredFeature, setHoveredFeature] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [leftHoveredFeature, setLeftHoveredFeature] = useState(null);
  const [leftMousePosition, setLeftMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (map && map.isStyleLoaded()) {
      setIsMapReady(true);
      setTimeout(() => map.resize(), 0);
    }
  }, [map]);

  useEffect(() => {
    if (!map || !isMapReady || compareMode) return;

    try {
      const sourceChanged = addRiskMapSource(map, tileVersion);
      if (sourceChanged || !map.getLayer("buildings")) {
        addRiskMapLayers(map, tilesScenario, tilesFilterLayers);
      }
    } catch (error) {
      console.error("Error adding risk map layers:", error);
    }
  }, [
    map,
    isMapReady,
    tilesFilterLayers,
    tilesScenario,
    tileVersion,
    compareMode,
  ]);

  useEffect(() => {
    return () => {
      if (map && !window.location.pathname.includes("/risk")) {
        removeRiskMapLayers(map);
        setHoveredFeature(null);

        const existingGeocoder = map._controls.find(
          (control) => control instanceof MapboxGeocoder
        );
        if (!existingGeocoder) {
          map.addControl(mapboxGeocoderControl, "top-right");
        }
      }
    };
  }, [map, mapboxGeocoderControl]);

  useEffect(() => {
    if (!map || !isMapReady || compareMode) return;
    updateLayerVisibility(map, tilesFilterLayers);
  }, [map, isMapReady, tilesFilterLayers, compareMode]);

  useEffect(() => {
    if (!map || !isMapReady || compareMode) return;
    updateScenarioFilters(map, tilesScenario);
  }, [map, isMapReady, tilesScenario, compareMode]);

  useEffect(() => {
    if (!map || !isMapReady) return;

    const handleMouseMove = (e) => {
      const features = map.queryRenderedFeatures(e.point, {
        layers: ["buildings"],
      });

      if (features.length > 0) {
        const feature = features[0];
        setHoveredFeature(feature.properties);
        setLeftHoveredFeature(null);
        const rect = map.getCanvas().getBoundingClientRect();
        setMousePosition({ x: rect.left + e.point.x, y: rect.top + e.point.y });
        map.getCanvas().style.cursor = "pointer";
      } else {
        setHoveredFeature(null);
        map.getCanvas().style.cursor = "";
      }
    };

    const handleMouseLeave = () => {
      setHoveredFeature(null);
      map.getCanvas().style.cursor = "";
    };

    map.on("mousemove", handleMouseMove);
    map.on("mouseleave", handleMouseLeave);

    return () => {
      if (map) {
        map.off("mousemove", handleMouseMove);
        map.off("mouseleave", handleMouseLeave);
        if (map.getCanvas()) {
          map.getCanvas().style.cursor = "";
        }
      }
    };
  }, [map, isMapReady]);

  const handleLeftMapHover = (hoverData) => {
    if (hoverData) {
      setLeftHoveredFeature(hoverData.feature);
      setLeftMousePosition(hoverData.position);
      setHoveredFeature(null);
    } else {
      setLeftHoveredFeature(null);
    }
  };

  return (
    <>
      {hoveredFeature &&
        hoveredFeature.total_score !== null &&
        hoveredFeature.total_score !== undefined && (
          <Box
            sx={{
              position: "fixed",
              left: mousePosition.x + 15,
              top: mousePosition.y - 60,
              backgroundColor: "#ffffff",
              color: "#000000",
              padding: "8px 12px",
              borderRadius: "4px",
              pointerEvents: "none",
              zIndex: 10000,
              fontSize: "14px",
              fontWeight: "bold",
              border: "1px solid #cccccc",
            }}
          >
            <div>Total Score: {hoveredFeature.total_score.toFixed(2)}</div>
            {hoveredFeature.north_score !== null &&
              hoveredFeature.north_score !== undefined && (
                <div>North Score: {hoveredFeature.north_score.toFixed(2)}</div>
              )}
            {hoveredFeature.south_score !== null &&
              hoveredFeature.south_score !== undefined && (
                <div>South Score: {hoveredFeature.south_score.toFixed(2)}</div>
              )}
          </Box>
        )}

      {leftHoveredFeature &&
        leftHoveredFeature.total_score !== null &&
        leftHoveredFeature.total_score !== undefined && (
          <Box
            sx={{
              position: "fixed",
              left: leftMousePosition.x + 15,
              top: leftMousePosition.y - 60,
              backgroundColor: "#ffffff",
              color: "#000000",
              padding: "8px 12px",
              borderRadius: "4px",
              pointerEvents: "none",
              zIndex: 10000,
              fontSize: "14px",
              fontWeight: "bold",
              border: "1px solid #cccccc",
            }}
          >
            <div>Total Score: {leftHoveredFeature.total_score.toFixed(2)}</div>
            {leftHoveredFeature.north_score !== null &&
              leftHoveredFeature.north_score !== undefined && (
                <div>
                  North Score: {leftHoveredFeature.north_score.toFixed(2)}
                </div>
              )}
            {leftHoveredFeature.south_score !== null &&
              leftHoveredFeature.south_score !== undefined && (
                <div>
                  South Score: {leftHoveredFeature.south_score.toFixed(2)}
                </div>
              )}
          </Box>
        )}

      <MapControlsAccordion
        type="risk"
        compareMode={compareMode}
        setCompareMode={setCompareMode}
        tileVersion={tileVersion}
        setTileVersion={setTileVersion}
        leftVersion={leftVersion}
        setLeftVersion={setLeftVersion}
        tilesScenario={tilesScenario}
        setTilesScenario={setTilesScenario}
        tilesFilterLayers={tilesFilterLayers}
        setTilesFilterLayers={setTilesFilterLayers}
      />

      <RiskComparison
        compareMode={compareMode}
        tileVersion={tileVersion}
        leftVersion={leftVersion}
        tilesScenario={tilesScenario}
        tilesFilterLayers={tilesFilterLayers}
        isMapReady={isMapReady}
        onLeftMapHover={handleLeftMapHover}
      />
    </>
  );
};

export default RiskMap;
